import { useEffect, useMemo, useState } from 'react'

import { getDashboardData } from '~/modules/dashboard/services'

import { LicenseComparisonChart } from './components/LicenseComparisonChart'
import { LicenseMonthlyEvolutionChart } from './components/LicenseMonthlyEvolutionChart'
import { LicensePopularityChart } from './components/LicensePopularityChart'
import { LicenseExtensionsCard } from './components/cards/LicenseExtensionsCard'
import { PersonalTrainerCard } from './components/cards/PersonalTrainerCard'
import { SalesCard } from './components/cards/SalesCard'
import { SubscriptionsCard } from './components/cards/SubscriptionsCard'
import { MonthlyEvolutionChart } from './components/MonthlyEvolutionChart'
import { PlansComparisonChart } from './components/PlansComparisonChart'
import { PlansPopularityChart } from './components/PlansPopularityChart'
import { DashboardData } from './components/types'
import { calculateTrend, formatCurrency } from './components/utils'

export function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    getDashboardData()
      .then((data) => {
        setData(data)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  const salesTrend = useMemo(() => {
    if (!data?.salesByMonth) return 0
    return calculateTrend(data?.salesByMonth)
  }, [data?.salesByMonth])

  const totalSales = useMemo(() => {
    if (!data?.salesByMonth) return 0
    return data?.salesByMonth.reduce((sum, month) => sum + month.amount, 0)
  }, [data?.salesByMonth])

  const lastMonthSales = useMemo(() => {
    if (!data?.salesByMonth || data.salesByMonth.length < 2) return 0
    return data.salesByMonth[data.salesByMonth.length - 2].amount
  }, [data?.salesByMonth])

  const currentMonthSales = useMemo(() => {
    if (!data?.salesByMonth || data.salesByMonth.length < 1) return 0
    return data.salesByMonth[data.salesByMonth.length - 1].amount
  }, [data?.salesByMonth])

  const activeSubscriptions = useMemo(() => {
    if (!data?.plansPopularity) return 0
    return data?.plansPopularity.reduce(
      (sum, plan) => sum + plan.activeNowCustomers,
      0,
    )
  }, [data?.plansPopularity])

  const newSubscriptions = useMemo(() => {
    if (!data?.subscriptionsByMonth || data.subscriptionsByMonth.length === 0)
      return 0
    const now = new Date()
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(now.getDate() - 30)
    const thirtyDaysAgoMonth = `${thirtyDaysAgo.getFullYear()}-${String(thirtyDaysAgo.getMonth() + 1).padStart(2, '0')}`
    return data.subscriptionsByMonth
      .filter((item) => item.month >= thirtyDaysAgoMonth)
      .reduce((sum, month) => sum + month.count, 0)
  }, [data?.subscriptionsByMonth])

  if (!data) {
    return (
      <div className='flex-1 flex items-center justify-center bg-slate-100'>
        <div className='text-xl font-semibold text-gray-600'>
          {loading ? 'Carregando...' : 'Erro ao carregar dados'}
        </div>
      </div>
    )
  }

  return (
    <div className='flex-1 flex flex-col p-5 bg-slate-100 overflow-hidden'>
      <div className='overflow-y-auto max-h-full'>
        <h1 className='text-2xl font-bold text-gray-800 mb-6'>
          Dashboard Administrativo
        </h1>

        {/* ── Section 1: Planos & Assinaturas ── */}
        <h2 className='text-lg font-semibold text-gray-600 mb-4'>
          Planos &amp; Assinaturas
        </h2>

        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-6'>
          <PersonalTrainerCard
            total={data.personalTrainers.total}
            activeCount={data.personalTrainers.withActiveSubscription}
            inactiveCount={data.personalTrainers.withoutActiveSubscription}
          />
          <SalesCard
            totalSales={formatCurrency(totalSales)}
            lastMonthSales={formatCurrency(lastMonthSales)}
            currentMonthSales={formatCurrency(currentMonthSales)}
            trend={salesTrend}
          />
          <SubscriptionsCard
            activeSubscriptions={activeSubscriptions}
            newSubscriptions={newSubscriptions}
          />
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6'>
          <MonthlyEvolutionChart
            salesData={data.salesByMonth}
            subscriptionsData={data.subscriptionsByMonth}
          />
          <PlansPopularityChart plansData={data.plansPopularity} />
        </div>

        <PlansComparisonChart plansData={data.plansPopularity} />

        {/* ── Section 2: Licenças Avulsas ── */}
        <h2 className='text-lg font-semibold text-gray-600 mb-4 mt-4'>
          Licenças Avulsas
        </h2>

        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-6'>
          <LicenseExtensionsCard data={data.licenseExtensions} />
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6'>
          <LicenseMonthlyEvolutionChart data={data.licenseExtensions.byMonth} />
          <LicensePopularityChart data={data.licenseExtensions.byExtension} />
        </div>

        <LicenseComparisonChart data={data.licenseExtensions.byExtension} />
      </div>
    </div>
  )
}
